// src/context/AuthContext.jsx
import { createContext, useContext, useEffect, useMemo, useState, useRef } from "react";
import { onAuthStateChanged, onIdTokenChanged } from "firebase/auth";
import { auth, loadFirestore, loadFunctions } from "../services/firebase";
import * as Sentry from '@sentry/react';

// --- Constantes / utilidades ---
const PREMIUM_ACTIVE_STATES = new Set(["active", "past_due"]);
const PENDING_TIMEOUT_MS = 20 * 60 * 1000; // 20 minutos
const FAILSAFE_PREMIUM_TIMEOUT_MS = 8000;   // 8s: evita spinner infinito si Firestore falla

function safeReadLocal(key) {
  try {
    if (typeof window === "undefined") return null;
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}
function safeRemoveLocal(key) {
  try {
    if (typeof window !== "undefined") window.localStorage.removeItem(key);
  } catch {}
}
function readPendingSubscription() {
  const raw = safeReadLocal("subscription_status");
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    const recent = Date.now() - parsed?.timestamp < PENDING_TIMEOUT_MS;
    if (parsed?.status === "pending" && recent) return { status: "pending" };
  } catch {}
  safeRemoveLocal("subscription_status");
  return null;
}

export function computePremiumState(premiumData, grantData) {
  if (premiumData && (premiumData.status === 'active' || premiumData.status === 'past_due')) {
    safeRemoveLocal("subscription_status");
    return { status: premiumData.status, details: premiumData };
  }

  if (premiumData?.status === 'expired' && (premiumData.inGracePeriod || premiumData.gracePeriodEndsAt)) {
    const graceEndsAt = premiumData.gracePeriodEndsAt?.toDate?.() || new Date(premiumData.gracePeriodEndsAt);
    if (graceEndsAt > new Date()) {
      safeRemoveLocal("subscription_status");
      return { status: "past_due", details: { ...premiumData, status: "past_due" } };
    }
  }

  if (premiumData?.status === 'cancelled' && premiumData.expiresAt) {
    const expiresAt = premiumData.expiresAt?.toDate?.() || new Date(premiumData.expiresAt);
    if (expiresAt > new Date()) {
      safeRemoveLocal("subscription_status");
      return { status: "active", details: { ...premiumData, status: "active", cancelled: true } };
    }
  }

  if (grantData && !grantData.used) {
    const expiresAt = grantData.expiresAt?.toDate?.() || new Date(grantData.expiresAt);
    if (expiresAt > new Date()) {
      return { status: "active", details: { status: "active", source: "freeGrant", expiresAt: grantData.expiresAt } };
    }
  }

  if (premiumData?.status === 'expired' && !premiumData.inGracePeriod && !premiumData.gracePeriodEndsAt) {
    safeRemoveLocal("subscription_status");
    return { status: null, details: null };
  }

  if (premiumData) {
    safeRemoveLocal("subscription_status");
    return { status: premiumData.status, details: premiumData };
  }

  if (!grantData || grantData.used) {
    const pending = readPendingSubscription();
    if (pending) return { status: "pending", details: { status: "pending" } };
  }

  return { status: null, details: null };
}

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  const [isImpersonating, setIsImpersonating] = useState(false);

  const [premiumStatus, setPremiumStatus] = useState(null);
  const [premiumDetails, setPremiumDetails] = useState(null);
  const [premiumLoading, setPremiumLoading] = useState(true);

  // --- Escucha de sesión (login / logout) ---
  useEffect(() => {
    let mounted = true;
    const unsubAuth = onAuthStateChanged(auth, (user) => {
      if (!mounted) return;
      setCurrentUser(user || null);
      setAuthLoading(false);
      Sentry.setUser(user ? { id: user.uid, email: user.email } : null);
    });
    return () => {
      mounted = false;
      unsubAuth();
    };
  }, []);

  const lastLoggedUserId = useRef(null);
  const lastLoginTime = useRef(null);
  const wasLoggedOut = useRef(true);
  const updateTimerRef = useRef(null);

  // --- Claims: detectar impersonación y registrar logueos ---
  useEffect(() => {
    if (!currentUser) {
      setIsImpersonating(false);
      lastLoggedUserId.current = null;
      lastLoginTime.current = null;
      wasLoggedOut.current = true;
      return;
    }
    let mounted = true;
    const unsubId = onIdTokenChanged(auth, async (user) => {
      if (!mounted) return;
      if (!user) {
        setIsImpersonating(false);
        lastLoggedUserId.current = null;
        lastLoginTime.current = null;
        wasLoggedOut.current = true;
        return;
      }
      try {
        const tokenRes = await user.getIdTokenResult();
        const isImpersonatingUser = Boolean(tokenRes.claims?.impersonatorUid);
        setIsImpersonating(isImpersonatingUser);

        const now = Date.now();
        const shouldLogLogin =
          !isImpersonatingUser &&
          (user.uid !== lastLoggedUserId.current ||
           wasLoggedOut.current ||
           (lastLoginTime.current && (now - lastLoginTime.current) > 5 * 60 * 1000));

        if (shouldLogLogin) {
          lastLoggedUserId.current = user.uid;
          lastLoginTime.current = now;
          wasLoggedOut.current = false;

          try {
            // loadFunctions hace dynamic import de firebase/functions: chunk separado
            const { functions, httpsCallable } = await loadFunctions();
            const logUserLogin = httpsCallable(functions, 'logUserLogin');
            logUserLogin().catch((error) => {
              console.error("[AuthContext] Error registrando login:", error);
            });
          } catch (error) {
            console.error("[AuthContext] Error llamando logUserLogin:", error);
          }
        }
      } catch (e) {
        console.error("[AuthContext] Error leyendo claims:", e);
        setIsImpersonating(false);
      }
    });
    return () => {
      mounted = false;
      unsubId();
    };
  }, [currentUser]);

  // --- Premium: dynamic import de Firestore + repos, sólo cuando hay user ---
  useEffect(() => {
    if (!currentUser) {
      setPremiumLoading(false);
      setPremiumStatus(null);
      setPremiumDetails(null);
      setIsAdmin(false);
      return;
    }

    setPremiumLoading(true);

    let mounted = true;
    let settled = false;
    const cleanups = [];

    // Failsafe inmediato (no espera al dynamic import): si todo falla, libera UI a los 8s
    const timeoutId = setTimeout(() => {
      if (!mounted || settled) return;
      settled = true;
      const pending = readPendingSubscription();
      if (pending) {
        setPremiumStatus("pending");
        setPremiumDetails({ status: "pending" });
      } else {
        setPremiumStatus(null);
        setPremiumDetails(null);
      }
      setPremiumLoading(false);
      console.warn("[AuthContext] Failsafe premium timeout activado (8s).");
    }, FAILSAFE_PREMIUM_TIMEOUT_MS);
    cleanups.push(() => clearTimeout(timeoutId));

    (async () => {
      // Estos imports producen chunks separados (firebase/firestore + repos).
      // Sólo se cargan cuando existe currentUser → la landing anónima no los baja.
      const [firestoreMod, premiumRepoMod, profilesRepoMod] = await Promise.all([
        import("firebase/firestore"),
        import("../repositories/premiumRepo"),
        import("../repositories/profilesRepo"),
        loadFirestore(),
      ]);

      if (!mounted) return;

      const { onSnapshot } = firestoreMod;
      const premiumRepo = premiumRepoMod;
      const profilesRepo = profilesRepoMod;

      let premiumData = null;
      let grantData = null;

      const applyPremiumState = () => {
        if (!settled) {
          settled = true;
          clearTimeout(timeoutId);
        }
        const { status, details } = computePremiumState(premiumData, grantData);
        setPremiumStatus(status);
        setPremiumDetails(details);
        setPremiumLoading(false);
      };

      const updatePremiumStatus = () => {
        clearTimeout(updateTimerRef.current);
        updateTimerRef.current = setTimeout(applyPremiumState, 50);
      };

      const premiumUnsub = onSnapshot(
        premiumRepo.premiumRef(currentUser.uid),
        (snap) => {
          premiumData = snap.exists() ? snap.data() : null;
          updatePremiumStatus();
        },
        (err) => {
          console.error("[AuthContext] onSnapshot usuariosPremium error:", err);
          premiumData = null;
          updatePremiumStatus();
        }
      );

      const grantsUnsub = onSnapshot(
        premiumRepo.activeGrantQuery(currentUser.uid),
        (snapshot) => {
          grantData = snapshot.empty ? null : snapshot.docs[0].data();
          updatePremiumStatus();
        },
        (err) => {
          console.error("[AuthContext] onSnapshot freePremiumGrants error:", err);
          grantData = null;
          updatePremiumStatus();
        }
      );

      const profileUnsub = onSnapshot(
        profilesRepo.profileRef(currentUser.uid),
        (snap) => {
          const profileData = snap.exists() ? snap.data() : null;
          setIsAdmin(profileData?.isAdmin === true);
          updatePremiumStatus();
        },
        (err) => {
          console.error("[AuthContext] onSnapshot profiles error:", err);
          setIsAdmin(false);
          updatePremiumStatus();
        }
      );

      cleanups.push(() => {
        if (premiumUnsub) premiumUnsub();
        if (grantsUnsub) grantsUnsub();
        if (profileUnsub) profileUnsub();
        clearTimeout(updateTimerRef.current);
      });
    })().catch((err) => {
      console.error("[AuthContext] Error cargando Firestore/repos:", err);
      if (mounted && !settled) {
        settled = true;
        clearTimeout(timeoutId);
        setPremiumLoading(false);
      }
    });

    return () => {
      mounted = false;
      cleanups.forEach((fn) => fn());
    };
  }, [currentUser]);

  const isPremium = useMemo(
    () => PREMIUM_ACTIVE_STATES.has(premiumStatus ?? ""),
    [premiumStatus]
  );

  const value = useMemo(
    () => ({
      currentUser,
      authLoading,
      isAdmin,
      isImpersonating,
      premiumStatus,
      premiumDetails,
      premiumLoading,
      isPremium,
    }),
    [
      currentUser,
      authLoading,
      isAdmin,
      isImpersonating,
      premiumStatus,
      premiumDetails,
      premiumLoading,
      isPremium,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
